/**
 * Map Preferences Store — user-level, persisted map presentation choices that
 * live in the profile menu alongside the theme (Appearance) preference.
 *
 * Currently holds the map PROJECTION (flat mercator ↔ globe). This used to be a
 * per-page toggle inside the Station Overview map header, but projection is a
 * personal viewing preference — not page state — so it was promoted here next to
 * the theme switch. The Station Overview map subscribes to `projection` and calls
 * map.setProjection() when it changes; the bezel's profile menu writes it.
 *
 * Persisted (zustand/persist) so the choice survives a reload, exactly like the
 * theme preference (stationExplorer_theme).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MapProjection = 'mercator' | 'globe';

interface MapPreferencesState {
  /** Flat (mercator) vs globe map projection. */
  projection: MapProjection;
  setProjection: (projection: MapProjection) => void;
  /** IANA timezone string for all time displays. Defaults to 'UTC'. */
  timezone: string;
  setTimezone: (timezone: string) => void;
  /** 24-hour (true) or 12-hour AM/PM (false) time format. Default: true (meteorological standard). */
  use24HourTime: boolean;
  setUse24HourTime: (v: boolean) => void;
}

const STORAGE_KEY = 'stationExplorer_mapPreferences';

export const useMapPreferencesStore = create<MapPreferencesState>()(
  persist(
    (set) => ({
      // Default to the globe so the Station Overview map opens on the "planet in
      // space" view (auto-rotating, themed starfield). A user who explicitly picks
      // Flat in the profile menu has that choice persisted (zustand/persist), so
      // their preference still overrides this default on the next load.
      projection: 'globe',
      setProjection: (projection) => set({ projection }),
      timezone: 'UTC',
      setTimezone: (timezone) => set({ timezone }),
      use24HourTime: true,
      setUse24HourTime: (use24HourTime) => set({ use24HourTime }),
    }),
    { name: STORAGE_KEY },
  ),
);
